import React, { useState, useEffect, useCallback } from 'react';
import { MoreHorizontal, Copy, Edit2, Trash2, MessageSquare, ChevronDown, ChevronRight, Send } from 'lucide-react';
import { Menu, MenuItem, MenuItems, MenuButton } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import { WorkItemComment } from 'types/task';
import { ProjectUser } from 'types/project';
import { getInitials } from 'utils/getInitials';
import { extractMentionIds } from 'utils/mentions';
import { usePmoNavigation } from 'hooks/usePmoNavigation';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';
import TipTapEditor from 'components/common/RichTextEditor/RichTextEditor';
import { RenderSavedContent } from 'components/common/RichTextEditor/RenderSavedContent';
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal';
import CommentEmojiPicker from './CommentEmojiPicker';
import CommentReactions from './CommentReactions';
import { getConnectorColor, getExactFormatDate, normalizeEmptyParagraphs, getMediaType } from 'utils/helpers';
import { useMediaUpload } from 'service/project/hooks/useMediaUpload';
import {
  useAddCommentMutation,
  useDeleteCommentMutation,
  useUpdateCommentMutation,
  useToggleCommentReactionMutation,
  useGetRepliesQuery,
} from 'service/project/api/workItem';

interface CommentThreadProps {
  comment: WorkItemComment;
  projectUuid: string;
  workItemUuid: string;
  projectKey: string;
  workItemKey: string;
  mentionUsers?: ProjectUser[];
  rootCommentUuid?: string;
}

const CommentThread: React.FC<CommentThreadProps> = ({
  comment,
  projectUuid,
  workItemUuid,
  projectKey,
  workItemKey,
  mentionUsers = [],
  rootCommentUuid,
}) => {
  const depth = comment.depth ?? 0;
  const isDeleted = comment.isDeletedPlaceholder ?? false;
  const hasReplies = comment.hasReplies || (comment.replyCount ?? 0) > 0;

  const { orgSlug } = usePmoNavigation();
  const location = useLocation();
  const currentUser = useAppSelector(selectAuthUser);
  const currentUserId = currentUser?.appUserUuid;

  const { mutate: submitComment, isPending: isSubmitting } = useAddCommentMutation();
  const { mutate: deleteComment, isPending: isDeleting } = useDeleteCommentMutation();
  const { mutate: updateComment, isPending: isUpdating } = useUpdateCommentMutation();
  const { mutate: toggleReaction } = useToggleCommentReactionMutation();
  const { mutateAsync: uploadMedia } = useMediaUpload();

  const handleVideoUpload = async (file: File) => {
    const result = await uploadMedia({
      file,
      projectUuid,
      workItemUuid,
      originalFileName: file.name,
      mediaType: getMediaType(file),
    });
    return { mediaUuid: result.mediaUuid, projectUuid, fileName: file.name };
  };

  // Expand/collapse replies - auto expand if this is the root of a deep-linked comment reply
  const [isExpanded, setIsExpanded] = useState(() => {
    if (depth > 0) return true;

    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (hash.startsWith('#comment-')) {
      const hashWithoutPrefix = hash.replace('#comment-', '');
      if (hashWithoutPrefix.includes('_root-')) {
        const parts = hashWithoutPrefix.split('_root-');
        const rootId = parts[parts.length - 1];
        if (rootId === comment.commentUuid) {
          return true;
        }
      } else {
        // No root ID provided in hash. If it's not this root comment itself,
        // expand all root comments to ensure the nested reply is found.
        if (hashWithoutPrefix !== comment.commentUuid) {
          return true;
        }
      }
    }
    return false;
  });

  // Update isExpanded if the hash changes (e.g. via SPA navigation)
  useEffect(() => {
    const hash = location.hash;
    if (depth === 0 && hash.startsWith('#comment-')) {
      const hashWithoutPrefix = hash.replace('#comment-', '');
      if (hashWithoutPrefix.includes('_root-')) {
        const parts = hashWithoutPrefix.split('_root-');
        const rootId = parts[parts.length - 1];
        if (rootId === comment.commentUuid) {
          setIsExpanded(true);
        }
      } else {
        if (hashWithoutPrefix !== comment.commentUuid) {
          setIsExpanded(true);
        }
      }
    }
  }, [location.hash, depth, comment.commentUuid]);

  // Current page of replies being fetched
  const [page, setPage] = useState(0);
  // All replies accumulated across pages
  const [allReplies, setAllReplies] = useState<WorkItemComment[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  // Inline reply composer
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState('');

  // Inline edit form
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');

  // Delete modal
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);

  // Load replies when expanded
  const { data: repliesPage, isLoading: isLoadingReplies } = useGetRepliesQuery(
    projectUuid,
    workItemUuid,
    comment.commentUuid,
    page,
    isExpanded,
  );

  // Accumulate pages into allReplies
  useEffect(() => {
    if (!repliesPage) return;
    if (page === 0) {
      setAllReplies(repliesPage.data ?? []);
    } else {
      setAllReplies((prev) => [...prev, ...(repliesPage.data ?? [])]);
    }
    setTotalPages(repliesPage.totalPages ?? 1);
  }, [repliesPage, page]);

  // ── Global search deep-link: scroll + flash highlight ─────────────────────
  // Fires whenever the URL hash changes (SPA navigation via React Router),
  // not just on mount — so it works whether the component is freshly mounted
  // or was already on screen when the user clicked a Comment Match result.
  useEffect(() => {
    const hash = location.hash;
    if (!hash.startsWith('#comment-')) return;
    
    const cleanCommentId = hash.replace('#comment-', '').split('_root-')[0];
    if (cleanCommentId !== comment.commentUuid) return;

    // Retry every 150 ms until the element is in the DOM (the task detail
    // page loads comments asynchronously, so we may arrive before render).
    let attempts = 0;
    const MAX = 25; // up to ~3.75 s

    const tryScroll = () => {
      const el = document.getElementById(`comment-${comment.commentUuid}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('search-comment-highlight');
        setTimeout(() => el.classList.remove('search-comment-highlight'), 1800);
        return;
      }
      if (++attempts < MAX) setTimeout(tryScroll, 150);
    };

    const timer = setTimeout(tryScroll, 150);
    return () => clearTimeout(timer);
  }, [location.hash, comment.commentUuid]);
  // ──────────────────────────────────────────────────────────────────────────

  // Reset state when collapsed
  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => {
      if (prev) {
        // Collapsing — reset page but keep data so re-open is instant
        return false;
      }
      setPage(0);
      return true;
    });
  }, []);

  const isReplyEmpty =
    !replyText ||
    replyText === '<p></p>' ||
    (replyText.replace(/<[^>]*>?/gm, '').trim() === '' && !replyText.includes('<img'));

  const handleSubmitReply = () => {
    if (isReplyEmpty || isSubmitting) return;
    submitComment(
      {
        commentUuid: crypto.randomUUID(),
        projectUuid,
        workItemUuid,
        commentDescription: replyText,
        mentionedAppUsersUuidList: extractMentionIds(replyText),
        parentCommentUuid: comment.commentUuid,
      },
      {
        onSuccess: () => {
          setReplyText('');
          setIsReplying(false);
          // Auto-expand to show the new reply
          setPage(0);
          setIsExpanded(true);
        },
      },
    );
  };

  const handleCopyLink = () => {
    const baseUrl = window.location.origin;
    const rootUuid = rootCommentUuid || (depth === 0 ? comment.commentUuid : comment.rootCommentUuid || comment.parentCommentUuid);
    const hash = rootUuid
      ? `#comment-${comment.commentUuid}_root-${rootUuid}`
      : `#comment-${comment.commentUuid}`;
    const shareUrl = `${baseUrl}/${orgSlug}/pmo/${projectKey}/${workItemKey}${hash}`;
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => toast.success('Link copied to clipboard'))
      .catch(() => toast.error('Failed to copy link'));
  };

  const handleToggleReaction = (emoji: string) => {
    toggleReaction({
      projectUuid,
      workItemUuid,
      commentUuid: comment.commentUuid,
      emoji,
      parentCommentUuid: comment.parentCommentUuid ?? undefined,
    });
  };

  const isOwner = !!(currentUserId && comment.commentedByAppUserUuid === currentUserId);
  // Connector colour used for the reply thread this comment expands
  const connectorColor = getConnectorColor(depth);

  return (
    <div
      id={`comment-${comment.commentUuid}`}
      className="relative"
    >

      {/* Comment card */}
      <div className="flex gap-3 group transition-all duration-200 p-2 -m-2 rounded-lg">
        {/* Avatar */}
        <div className={`w-8 h-8 bg-primary-subtle rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${isDeleted ? 'opacity-60' : ''}`}>
          {isDeleted ? '?' : getInitials(comment.commentedByAppUserFullName)}
        </div>

        <div className="flex-1 min-w-0">
          <div className={isDeleted ? 'opacity-60' : ''}>
            {/* Header row */}
            <div className="flex items-start justify-between gap-4 mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              {!isDeleted && (
                <span className="font-medium text-neutral text-sm">
                  {comment.commentedByAppUserFullName}
                </span>
              )}
              <span className="text-neutral-medium text-xs">
                {isDeleted ? 'Deleted' : getExactFormatDate(comment.commentedAt)}
              </span>
              {comment.isEdited && !isDeleted && (
                <span className="text-[10px] bg-primary-subtle text-neutral-medium px-2 py-0.5 rounded-full">
                  Edited
                </span>
              )}

              {!isDeleted && (
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={handleCopyLink}
                    className="p-1 rounded-md text-neutral-medium hover:text-blue-400 transition-colors"
                    title="Copy link"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <CommentEmojiPicker onSelect={(emoji) => handleToggleReaction(emoji)} />
                </div>
              )}
            </div>

            {/* Options menu — only for owner, not on deleted placeholder */}
            {isOwner && !isDeleted && (
              <Menu as="div" className="relative inline-block text-left flex-shrink-0">
                <MenuButton className="p-1 rounded-md border border-blue-500/30 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors flex items-center justify-center">
                  <MoreHorizontal className="w-4 h-4" />
                </MenuButton>
                <MenuItems
                  anchor="bottom end"
                  className="absolute right-0 mt-1 w-44 origin-top-right rounded-lg bg-primary-variant border border-primary-subtle shadow-lg focus:outline-none z-20 overflow-hidden"
                >
                  <div className="p-1">
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          onClick={() => {
                            setIsEditing(true);
                            setEditText(comment.commentDescription);
                          }}
                          className={`${
                            focus ? 'bg-primary-subtle text-neutral' : 'text-neutral-medium'
                          } group flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors`}
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                      )}
                    </MenuItem>
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          onClick={() => setCommentToDelete(comment.commentUuid)}
                          className={`${
                            focus ? 'bg-red-500/10 text-red-500' : 'text-red-500/80'
                          } group flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            )}
          </div>

          {/* Body — tombstone or content */}
          {isDeleted ? (
            <div className="text-sm text-neutral-medium italic py-1 px-3 border border-dashed border-primary-subtle rounded-md inline-block">
              This comment was deleted.
            </div>
          ) : isEditing ? (
            <div className="mt-2 w-full border border-primary-subtle rounded-lg overflow-hidden bg-primary-variant">
              <TipTapEditor
                initialContent={editText}
                onChange={(c) => setEditText(c)}
                placeholder="Edit comment..."
                className={isUpdating ? 'opacity-50 pointer-events-none min-h-[60px]' : 'min-h-[60px]'}
                mentionUsers={mentionUsers}
                isComment={true}
                onVideoUpload={handleVideoUpload}
              />
              <div className="flex justify-end gap-2 p-2 bg-primary">
                <button
                  onClick={() => { setIsEditing(false); setEditText(''); }}
                  disabled={isUpdating}
                  className="px-3 py-1.5 text-xs font-medium text-neutral-medium hover:text-neutral hover:bg-primary-subtle rounded transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!editText.trim() || editText === comment.commentDescription) {
                      setIsEditing(false);
                      return;
                    }
                    updateComment(
                      {
                        projectUuid,
                        workItemUuid,
                        commentUuid: comment.commentUuid,
                        commentDescription: editText,
                        mentionedAppUsersUuidList: extractMentionIds(editText),
                        parentCommentUuid: comment.parentCommentUuid ?? undefined,
                      },
                      { onSuccess: () => { setIsEditing(false); setEditText(''); } },
                    );
                  }}
                  disabled={isUpdating || !editText.trim()}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <RenderSavedContent
              className="text-neutral-light text-sm leading-relaxed prose prose-sm prose-invert max-w-none mt-1 overflow-x-auto break-words"
              html={normalizeEmptyParagraphs(comment.commentDescription)}
            />
          )}

          {/* Reactions */}
          {!isDeleted && comment.reactions && comment.reactions.length > 0 && (
            <CommentReactions
              reactions={comment.reactions}
              onToggle={(emoji) => handleToggleReaction(emoji)}
            />
          )}
          </div>

          {/* Action bar: Reply + View Replies */}
          {!isEditing && (
            <div className="flex items-center gap-4 mt-2">
              {/* Reply button — hidden on deleted placeholder */}
              {!isDeleted && (
                <button
                  onClick={() => setIsReplying((prev) => !prev)}
                  className="text-xs font-medium text-blue-400/80 hover:text-blue-400 transition-colors flex items-center gap-1"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Reply
                </button>
              )}

              {/* Expand replies toggle */}
              {hasReplies && depth === 0 && (
                <button
                  onClick={handleToggleExpand}
                  className="text-xs font-semibold text-blue-400/90 hover:text-blue-400 transition-colors flex items-center gap-1"
                >
                  {isExpanded ? (
                    <>
                      <ChevronDown className="w-3.5 h-3.5" />
                      Hide replies
                    </>
                  ) : (
                    <>
                      <ChevronRight className="w-3.5 h-3.5" />
                      {comment.replyCount && comment.replyCount > 0
                        ? `View ${comment.replyCount} ${comment.replyCount === 1 ? 'reply' : 'replies'}`
                        : 'View replies'}
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Inline reply composer */}
          {isReplying && (
            <div className="mt-3 flex gap-2">
              <div className="w-7 h-7 bg-primary-subtle rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                {getInitials(currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Y')}
              </div>
              <div className="flex-1">
                <div className="border border-primary-subtle rounded-lg bg-primary-variant overflow-hidden">
                  <TipTapEditor
                    initialContent={replyText}
                    onChange={(c) => setReplyText(c)}
                    placeholder={`Reply to ${isDeleted ? 'comment' : comment.commentedByAppUserFullName}... (Type @ to mention)`}
                    className={isSubmitting ? 'opacity-50 pointer-events-none min-h-[50px]' : 'min-h-[50px]'}
                    mentionUsers={mentionUsers}
                    isComment={true}
                    onVideoUpload={handleVideoUpload}
                  />
                </div>
                <div className="flex items-center justify-end gap-2 mt-2">
                  <button
                    onClick={() => { setIsReplying(false); setReplyText(''); }}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-xs text-neutral-medium hover:text-neutral hover:bg-primary-subtle rounded transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitReply}
                    disabled={isReplyEmpty || isSubmitting}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                      !isReplyEmpty && !isSubmitting
                        ? 'button-active'
                        : 'button-inactive opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? 'Posting...' : 'Reply'}
                    {!isSubmitting && <Send className="w-3 h-3" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Expanded replies list — curved L-shape connector */}
          {isExpanded && (
            <div 
              className={`comment-replies-wrapper ${depth >= 5 ? 'depth-cap-active' : ''}`}
            >
              {/* Vertical line + curved bottom (the L-shape) */}
              {(allReplies.length > 0 || isLoadingReplies) && (
                <div
                  style={{
                    position: 'absolute',
                    left: '3px',
                    top: '8px',
                    width: '16px',
                    height: 'calc(100% - 28px)',
                    borderLeft: `2px solid ${connectorColor}`,
                    borderBottom: `2px solid ${connectorColor}`,
                    borderBottomLeftRadius: '10px',
                    opacity: 0.55,
                    pointerEvents: 'none',
                  }}
                />
              )}

              {isLoadingReplies && allReplies.length === 0 ? (
                <div className="flex items-center gap-2 py-2 text-xs text-neutral-medium animate-pulse">
                  <div className="w-4 h-4 rounded-full bg-primary-subtle" />
                  Loading replies...
                </div>
              ) : (
                <div className="space-y-4">
                  {allReplies.map((reply) => (
                    <CommentThread
                      key={reply.commentUuid}
                      comment={reply}
                      projectUuid={projectUuid}
                      workItemUuid={workItemUuid}
                      projectKey={projectKey}
                      workItemKey={workItemKey}
                      mentionUsers={mentionUsers}
                      rootCommentUuid={rootCommentUuid || comment.commentUuid}
                    />
                  ))}

                  {page < totalPages - 1 && (
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={isLoadingReplies}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      {isLoadingReplies ? 'Loading...' : 'Load more replies'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      <ConfirmDeleteModal
        isOpen={!!commentToDelete}
        onClose={() => setCommentToDelete(null)}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        isDeleting={isDeleting}
        onConfirm={() => {
          if (commentToDelete) {
            deleteComment(
              {
                projectUuid,
                workItemUuid,
                commentUuid: commentToDelete,
                parentCommentUuid: comment.parentCommentUuid ?? undefined,
              },
              { onSuccess: () => setCommentToDelete(null) },
            );
          }
        }}
      />
    </div>
  );
};

export default CommentThread;
